import { Checkbox, Divider, Input, InputNumber, Radio, Select, Space } from 'antd';
import { SalaryInputRange } from '../SalaryInput/SalaryInputRange';
import React from 'react';
import { ScreenQuestionCardLayout } from './ScreenQuestionCardLayout';
import {
  NOTICE_PERIOD_OPTIONS,
  NOTICE_PERIOD_VALUE_MAP,
  createNoticePeriodQuestion,
} from './noticePeriodConfig';
import { answerFromSalaryRange, salaryRangeFromAnswer } from './expectedSalaryConfig';
import { getSkillExperienceQuestionText } from './skillExperienceConfig';
import { EDUCATION_QUESTION_TEXT } from './educationConfig';
import {
  LANGUAGE_QUESTION_TEXT,
  LANGUAGE_PROFICIENCY_VALUE_MAP,
  LANGUAGE_PROFICIENCY_SCALE,
} from './languageConfig';
import type { NumericAnswerRange } from './types';

const experience = [
  { label: '1 Year', value: 1 },
  { label: '2 Years', value: 2 },
  { label: '3 Years', value: 3 },
  { label: '4 Years', value: 4 },
  { label: '5 Years', value: 5 },
  { label: '5 Years+', value: 6 },
];

const parseYearsValue = (value: unknown): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value) && value > 0) return value;
  if (typeof value === 'string') {
    const hasPlus = value.includes('+');
    const match = value.match(/(\d+)/);
    if (match) {
      const parsed = Number(match[1]);
      if (!Number.isNaN(parsed) && parsed > 0) {
        return hasPlus ? parsed + 1 : parsed;
      }
    }
  }
  return undefined;
};

export const WorkExperienceCard: React.FC<any> = ({
  title = 'Work Experience',
  onDelete,
  size = 'middle',
  value,
  onChange,
}) => {
  const v = {
    jobFunction: '',
    expectedAnswer: undefined,
    answerType: 'numeric',
    required: false,
    ...value,
  };

  const set = (patch: Partial<typeof v>) => {
    const next = {
      ...v,
      ...patch,
      answerType: 'numeric',
    };
    delete (next as any).yearsRequired;
    onChange?.(next);
  };

  const selectedYears = parseYearsValue(v.expectedAnswer) ?? parseYearsValue(v.yearsRequired);

  return (
    <ScreenQuestionCardLayout title={title} onDelete={onDelete} className="border">
      <div className="flex flex-wrap items-center gap-2 py-2">
        <span className="text-gray-700">How many years of</span>
        <Input
          size={size}
          placeholder="Please enter name of the job function"
          className="max-w-[320px]"
          value={v.jobFunction}
          onChange={(e) => set({ jobFunction: e.target.value })}
        />
        <span className="text-gray-700">experience do you have?</span>
      </div>

      <div className="flex flex-wrap items-center gap-2 py-3 border-t">
        <span className="text-gray-700">This job requires</span>
        <InputNumber
          size={size}
          className="!w-[177px]"
          placeholder="Years"
          min={0}
          value={selectedYears}
          onChange={(val) => set({ expectedAnswer: val ?? undefined })}
        />
        <span className="text-gray-700">years minimum experience.</span>
      </div>

      <div className="pt-2 border-t text-base">
        <Checkbox checked={v.required} onChange={(e) => set({ required: e.target.checked })}>
          <span className="text-base">Required</span>
        </Checkbox>
      </div>
    </ScreenQuestionCardLayout>
  );
};

export const SkillCard: React.FC<any> = ({
  title = 'Skills',
  onDelete,
  size = 'middle',
  value,
  onChange,
}) => {
  const v = {
    jobFunction: '',
    expectedAnswer: undefined,
    answerType: 'numeric',
    required: false,
    ...value,
  };

  const set = (patch: Partial<typeof v>) => {
    const nextJobFunction = patch.jobFunction !== undefined ? patch.jobFunction : v.jobFunction;
    const next = {
      ...v,
      ...patch,
      answerType: 'numeric',
      question: getSkillExperienceQuestionText(nextJobFunction),
    };
    delete (next as any).yearsRequired;
    onChange?.(next);
  };

  const selectedYears = parseYearsValue(v.expectedAnswer) ?? parseYearsValue(v.yearsRequired);
  const questionText = getSkillExperienceQuestionText(v.jobFunction);

  return (
    <ScreenQuestionCardLayout title={title} onDelete={onDelete} className="border">
      <div className="flex flex-col gap-2 py-2">
        <span className="text-gray-700">{questionText}</span>
        <Input
          size={size}
          placeholder="Please enter Skill"
          className="max-w-[320px]"
          value={v.jobFunction}
          onChange={(e) => set({ jobFunction: e.target.value })}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2 py-3 border-t">
        <span className="text-gray-700">Expected answer (minimum) </span>
        <InputNumber
          size={size}
          className="!w-[177px]"
          placeholder="Years"
          min={0}
          value={selectedYears}
          onChange={(val) => set({ expectedAnswer: val ?? undefined })}
        />
      </div>

      <div className="pt-2 border-t text-base">
        <Checkbox checked={v.required} onChange={(e) => set({ required: e.target.checked })}>
          <span className="text-base">Required</span>
        </Checkbox>
      </div>
    </ScreenQuestionCardLayout>
  );
};

export const WorkModeCard: React.FC<any> = ({
  title = 'Work Mode',
  onDelete,
  size = 'middle',
  value,
  onChange,
}) => {
  const v = {
    expectedAnswer: '',
    answerType: 'SingleChoice',
    required: false,
    options: [
      { label: 'Remote', value: 'Remote' },
      { label: 'Hybrid', value: 'Hybrid' },
      { label: 'Onsite', value: 'Onsite' },
    ],
    ...value,
  };

  const set = (patch: Partial<typeof v>) => {
    const next = {
      ...v,
      ...patch,
      answerType: 'SingleChoice',
    };
    onChange?.(next);
  };

  const options = Array.isArray(v.options) && v.options.length > 0 ? v.options : [];

  return (
    <ScreenQuestionCardLayout title={title} onDelete={onDelete} className="border">
      <div className="flex flex-col gap-2 py-2">
        <span className="text-gray-700">
          {v.question ?? 'Which work mode are you comfortable with?'}
        </span>
        <Select
          size={size}
          className="!w-[220px]"
          placeholder="Select work mode"
          value={v.expectedAnswer ?? undefined}
          onChange={(val) => set({ expectedAnswer: val })}
          options={options}
        />
      </div>

      <div className="pt-2 border-t text-base">
        <Checkbox checked={v.required} onChange={(e) => set({ required: e.target.checked })}>
          <span className="text-base">Required</span>
        </Checkbox>
      </div>
    </ScreenQuestionCardLayout>
  );
};

export const EducationCard: React.FC<any> = ({
  title = 'Education',
  onDelete,
  size = 'middle',
  value,
  onChange,
}) => {
  const v = {
    educationLevel: '',
    expectedAnswer: '',
    expectedYesNo: '',
    answerType: 'SingleChoice',
    required: false,
    ...value,
  };

  const set = (patch: Partial<typeof v>) => {
    const nextLevel = patch.educationLevel ?? v.educationLevel;
    const rawExpected =
      patch.expectedAnswer ?? patch.expectedYesNo ?? v.expectedAnswer ?? v.expectedYesNo ?? '';
    const normalizedAnswer = typeof rawExpected === 'string' ? rawExpected : '';
    const next = {
      ...v,
      ...patch,
      answerType: 'YesNo',
      educationLevel: nextLevel,
      expectedAnswer: normalizedAnswer,
      expectedYesNo: normalizedAnswer,
      question: v.question ?? EDUCATION_QUESTION_TEXT,
    };
    onChange?.(next);
  };

  const questionText = v.question ?? EDUCATION_QUESTION_TEXT;

  return (
    <ScreenQuestionCardLayout title={title} onDelete={onDelete} className="border">
      <div className="flex flex-col gap-2 py-2">
        <span className="text-gray-700">{questionText}</span>
        <Input
          size={size}
          placeholder="Enter required education level"
          className="max-w-[320px]"
          value={v.educationLevel}
          onChange={(e) => set({ educationLevel: e.target.value })}
        />
      </div>

      <div className="py-3 border-t">
        <div className="text-base text-gray-500 mb-2">
          <span className="text-red-500">*</span> Expected Answer
        </div>
        <Radio.Group
          value={v.expectedAnswer || v.expectedYesNo}
          onChange={(e) => set({ expectedAnswer: e.target.value, expectedYesNo: e.target.value })}>
          <Space size="large">
            <Radio value="Yes">
              <span className="text-base">Yes</span>
            </Radio>
            <Radio value="No">
              <span className="text-base">No</span>
            </Radio>
          </Space>
        </Radio.Group>
      </div>

      <div className="pt-2 border-t ml-3 text-base">
        <Checkbox checked={v.required} onChange={(e) => set({ required: e.target.checked })}>
          <span className="text-base">Required</span>
        </Checkbox>
      </div>
    </ScreenQuestionCardLayout>
  );
};

export const LanguageCard: React.FC<any> = ({
  title = 'Language',
  onDelete,
  size = 'middle',
  value,
  onChange,
}) => {
  const v = {
    language: '',
    expectedAnswer: '',
    proficiency: '',
    answerType: 'SingleChoice',
    required: false,
    ...value,
  };

  const set = (patch: Partial<typeof v>) => {
    const nextLanguage = patch.language ?? v.language;
    const rawExpected =
      patch.expectedAnswer ?? patch.proficiency ?? v.expectedAnswer ?? v.proficiency ?? '';
    const normalizedAnswer = typeof rawExpected === 'string' ? rawExpected : '';
    const next = {
      ...v,
      ...patch,
      answerType: 'SingleChoice',
      language: nextLanguage,
      question: LANGUAGE_QUESTION_TEXT,
      expectedAnswer: normalizedAnswer,
      proficiency: normalizedAnswer,
      idealWeight: normalizedAnswer ? LANGUAGE_PROFICIENCY_VALUE_MAP[normalizedAnswer] : undefined,
      valueMap: LANGUAGE_PROFICIENCY_VALUE_MAP,
      options: LANGUAGE_PROFICIENCY_SCALE.map(({ label, value }) => ({ label, value })),
    };
    onChange?.(next);
  };

  const selectedAnswer = v.expectedAnswer || v.proficiency;

  return (
    <ScreenQuestionCardLayout title={title} onDelete={onDelete} className="border">
      <div className="flex flex-wrap items-center gap-2 py-2">
        <span className="text-gray-700">What is your level of proficiency in</span>
        <Select
          size={size}
          className="max-w-[200px]"
          placeholder="Select language"
          value={v.language}
          onChange={(val) => set({ language: val })}
          options={[
            { label: 'English', value: 'English' },
            { label: 'Sinhala', value: 'Sinhala' },
            { label: 'Tamil', value: 'Tamil' },
          ]}
        />
        <span className="text-gray-700">?</span>
      </div>

      <div className="py-3 border-t">
        <div className="text-base text-gray-500 mb-2">
          <span className="text-red-500">*</span> Expected Answer
        </div>
        <Radio.Group
          value={selectedAnswer}
          onChange={(e) => set({ expectedAnswer: e.target.value })}>
          {LANGUAGE_PROFICIENCY_SCALE.map((opt) => (
            <Radio key={opt.value} value={opt.value}>
              <span className="text-base">{opt.label}</span>
            </Radio>
          ))}
        </Radio.Group>
      </div>

      <div className="pt-2 border-t ml-3 text-base">
        <Checkbox checked={v.required} onChange={(e) => set({ required: e.target.checked })}>
          <span className="text-base">Required</span>
        </Checkbox>
      </div>
    </ScreenQuestionCardLayout>
  );
};

export const ExpectedSalaryCard: React.FC<any> = ({
  title = 'Expected Salary',
  onDelete,
  value,
  onChange,
}) => {
  const legacySalary = (value as any)?.salary;
  const v = {
    expectedAnswer: undefined,
    required: false,
    ...value,
  };
  delete (v as any).salary;
  const expectedAnswerValue = v.expectedAnswer;

  const set = (patch: Partial<typeof v>) => {
    const next = { ...v, ...patch };
    const sanitized = { ...next } as Record<string, unknown>;
    delete sanitized.salary;
    onChange?.(sanitized);
  };

  const looksNumericAnswer =
    expectedAnswerValue &&
    typeof expectedAnswerValue === 'object' &&
    (Object.prototype.hasOwnProperty.call(expectedAnswerValue, 'min') ||
      Object.prototype.hasOwnProperty.call(expectedAnswerValue, 'max'));

  const looksSalaryAnswer =
    expectedAnswerValue &&
    typeof expectedAnswerValue === 'object' &&
    (Object.prototype.hasOwnProperty.call(expectedAnswerValue, 'amount') ||
      Object.prototype.hasOwnProperty.call(expectedAnswerValue, 'fromAmount') ||
      Object.prototype.hasOwnProperty.call(expectedAnswerValue, 'salary') ||
      Object.prototype.hasOwnProperty.call(expectedAnswerValue, 'fromSalary') ||
      Object.prototype.hasOwnProperty.call(expectedAnswerValue, 'type'));

  const numericAnswer: NumericAnswerRange | undefined = looksNumericAnswer
    ? (expectedAnswerValue as NumericAnswerRange)
    : undefined;

  const legacyAnswer =
    !numericAnswer && looksSalaryAnswer
      ? answerFromSalaryRange(expectedAnswerValue as any)
      : !numericAnswer && legacySalary
      ? answerFromSalaryRange(legacySalary)
      : undefined;

  const salaryValue =
    salaryRangeFromAnswer(numericAnswer ?? legacyAnswer) ?? (legacySalary as any | undefined);

  return (
    <ScreenQuestionCardLayout title={title} onDelete={onDelete} className="border">
      <div className="text-gray-700 mb-1">{v.question}</div>
      <div className="flex w-full gap-3 py-2">
        <SalaryInputRange
          value={salaryValue}
          onChange={(salaryValue) =>
            set({
              expectedAnswer: answerFromSalaryRange(salaryValue),
            })
          }
        />
      </div>

      <div className="pt-2 border-t ml-3 text-base">
        <Checkbox checked={v.required} onChange={(e) => set({ required: e.target.checked })}>
          <span className="text-base">Required</span>
        </Checkbox>
      </div>
    </ScreenQuestionCardLayout>
  );
};

export const CurrentSalaryCard: React.FC<any> = ({
  title = 'Current Salary',
  onDelete,
  value,
  onChange,
}) => {
  const v = {
    expectedAnswer: 'Month',
    required: false,
    ...value,
  };

  const set = (patch: Partial<typeof v>) => {
    const next = { ...v, ...patch };
    delete (next as any).salary;
    delete (next as any).salaryType;
    delete (next as any).currency;
    onChange?.(next);
  };

  const salaryTypeOptions = [
    { label: 'Per Month', value: 'Month' },
    { label: 'Per Day', value: 'Day' },
    { label: 'Per Hour', value: 'Hour' },
    { label: 'Per Year', value: 'Year' },
  ];

  const selectedType =
    typeof v.expectedAnswer === 'string' && v.expectedAnswer.length > 0
      ? v.expectedAnswer
      : 'Month';

  return (
    <ScreenQuestionCardLayout title={title} onDelete={onDelete} className="border">
      <div className="text-gray-700 mb-1">{v.question}</div>
      <div className="flex flex-col gap-2 py-2">
        <Select
          className="w-full md:w-[240px]"
          value={selectedType}
          onChange={(val) => set({ expectedAnswer: val })}
          options={salaryTypeOptions}
        />
      </div>

      <div className="pt-2 border-t ml-3 text-base">
        <Checkbox checked={v.required} onChange={(e) => set({ required: e.target.checked })}>
          <span className="text-base">Required</span>
        </Checkbox>
      </div>
    </ScreenQuestionCardLayout>
  );
};

/* -------------------------------- Notice Period ---------------------------- */
export const NoticePeriodCard: React.FC<any> = ({
  title,
  onDelete,
  size = 'middle',
  value,
  onChange,
}) => {
  const defaults = createNoticePeriodQuestion({ title });
  const v = {
    ...defaults,
    ...value,
  };

  const set = (patch: Partial<typeof v>) => {
    const next = {
      ...v,
      ...patch,
    };
    const normalized = {
      ...next,
      title: title ?? next.title ?? defaults.title,
      type: 'NoticePeriod',
      category: next.category ?? 'PreDefined',
      question: next.question ?? defaults.question,
      answerType: defaults.answerType,
      valueMap: NOTICE_PERIOD_VALUE_MAP,
      options: NOTICE_PERIOD_OPTIONS,
    };
    const sanitized = { ...normalized } as Record<string, unknown>;
    delete sanitized.noticePeriod;
    onChange?.(sanitized);
  };

  const legacyNoticePeriod =
    typeof (value as any)?.noticePeriod === 'string' ? (value as any).noticePeriod : '';
  const selectedAnswer =
    typeof v.expectedAnswer === 'string' && v.expectedAnswer.length > 0
      ? v.expectedAnswer
      : legacyNoticePeriod;

  return (
    <ScreenQuestionCardLayout title={title} onDelete={onDelete} className="border">
      <div className="py-2">
        <div className="text-gray-700 mb-1">{v.question ?? 'What is your notice period?'}</div>
        <Select
          size={size}
          className="w-full md:w-[320px]"
          placeholder="Please select your expected time period"
          value={selectedAnswer || undefined}
          onChange={(val) => {
            const answer = typeof val === 'string' ? val : '';
            set({
              expectedAnswer: answer,
              idealWeight: answer ? NOTICE_PERIOD_VALUE_MAP[answer] : undefined,
            });
          }}
          options={NOTICE_PERIOD_OPTIONS}
        />
      </div>

      <div className="pt-2 border-t ml-3 !text-base">
        <Checkbox checked={v.required} onChange={(e) => set({ required: e.target.checked })}>
          <span className="text-base">Required</span>
        </Checkbox>
      </div>
    </ScreenQuestionCardLayout>
  );
};

export const YesNoCard: React.FC<any> = ({
  title,
  onDelete,
  className = '',
  question,
  value,
  onChange,
}) => {
  const v = {
    expectedYesNo: '',
    expectedAnswer: '',
    answerType: 'YesNo',
    required: false,
    ...value,
  };

  const set = (patch: Partial<typeof v>) => {
    const next = { ...v, ...patch };
    const expected =
      typeof next.expectedYesNo === 'string' && next.expectedYesNo.length > 0
        ? next.expectedYesNo
        : typeof next.expectedAnswer === 'string'
        ? next.expectedAnswer
        : '';
    next.answerType = 'YesNo';
    next.expectedYesNo = expected;
    next.expectedAnswer = expected;
    onChange?.(next);
  };

  return (
    <ScreenQuestionCardLayout title={title} onDelete={onDelete} className="border">
      <div
        className={`relative rounded-2xl border border-gray-200 bg-white p-4 shadow-sm ${className}`}>
        <Space.Compact className="w-full">
          <p className="mt-0">{question}</p>
        </Space.Compact>

        <div className="mt-3">
          <div className="text-base text-gray-500 mb-2">
            <span className="text-red-500">*</span> Expected Answer
          </div>
          <Radio.Group
            value={v.expectedYesNo}
            onChange={(e) => set({ expectedYesNo: e.target.value })}>
            <Space size="large">
              <Radio value="Yes">
                <span className="text-base">Yes</span>
              </Radio>
              <Radio value="No">
                <span className="text-base">No</span>
              </Radio>
            </Space>
          </Radio.Group>
        </div>

        <Divider className="my-3" />

        <div className="pt-2 border-t ml-3 text-base">
          <Checkbox checked={v.required} onChange={(e) => set({ required: e.target.checked })}>
            <span className="text-base">Required</span>
          </Checkbox>
        </div>
      </div>
    </ScreenQuestionCardLayout>
  );
};
